// ✅ MIGRATED TO V2 DESIGN SYSTEM - Volvo/Teal Theme
import React, { useState, useEffect } from 'react';
import { DollarSign, CheckCircle, AlertCircle, Loader2, Eye, EyeOff, X } from 'lucide-react';
import { notify } from '@/stores/notificationStore';
import { paypalAPI } from '@/utils/api';
import { logger } from '@/utils/logger';
import { Button, Spinner } from '@/components/ui-v2';
import { confirm } from '@/stores/confirmStore';

interface PayPalStatus {
  enabled: boolean;
  configured: boolean;
  onboardingCompleted: boolean;
  mode: 'sandbox' | 'live';
  hasMerchantId: boolean;
}

export const PayPalSettings: React.FC = () => {
  const [status, setStatus] = useState<PayPalStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [configuring, setConfiguring] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [merchantId, setMerchantId] = useState('');
  const [mode, setMode] = useState<'sandbox' | 'live'>('sandbox');
  const [showSecret, setShowSecret] = useState(false);

  // V2 Card classes - avec break-inside-avoid pour le layout masonry
  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl
    transition-all duration-300 break-inside-avoid mb-6`;

  const inputClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none text-sm`;

  const selectClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none text-sm cursor-pointer`;

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const data = await paypalAPI.getStatus();
      if (data.success) {
        setStatus(data.data);
        setMode(data.data.mode || 'sandbox');
      }
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Impossible de récupérer le statut PayPal', 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigure = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!clientId || !clientSecret) {
      notify.error('Veuillez renseigner le Client ID et le Client Secret', 'Erreur');
      return;
    }

    try {
      setConfiguring(true);
      const data = await paypalAPI.configure({
        clientId,
        clientSecret,
        merchantId: merchantId || null,
        mode,
      });

      if (data.success) {
        notify.success('PayPal configuré avec succès !', 'Succès');
        setShowForm(false);
        setClientId('');
        setClientSecret('');
        setMerchantId('');
        await fetchStatus();
      }
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de la configuration', 'Erreur');
    } finally {
      setConfiguring(false);
    }
  };

  const handleDisable = async () => {
    const confirmed = await confirm({
      title: 'Desactiver PayPal',
      message: 'Etes-vous sur de vouloir desactiver PayPal ?',
      variant: 'danger',
      confirmText: 'Desactiver',
      cancelText: 'Annuler',
    });
    if (!confirmed) {
      return;
    }

    try {
      const data = await paypalAPI.disable();
      if (data.success) {
        notify.success('PayPal désactivé', 'Succès');
        await fetchStatus();
      }
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de la désactivation', 'Erreur');
    }
  };

  if (loading) {
    return (
      <div className={cardClasses}>
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className={cardClasses}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-teal-100 rounded-xl">
          <DollarSign className="w-5 h-5 text-teal-600" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">Paiements PayPal</h2>
      </div>

      <div className="space-y-4">
        {/* Explication */}
        <div className="bg-teal-50/50 backdrop-blur-sm border border-teal-200/50 rounded-2xl p-4 hover:bg-teal-50/70 transition-all duration-300">
          <h3 className="font-semibold text-teal-900 mb-2">
            Comment ça marche ?
          </h3>
          <p className="text-sm text-teal-800">
            Configurez PayPal pour recevoir les paiements de vos factures directement sur votre compte PayPal.
            Les clients pourront payer via PayPal en plus ou à la place de Stripe.
          </p>
        </div>

        {/* Statut - Configuration requise */}
        {status && !status.configured && (
          <div className="bg-orange-50/50 backdrop-blur-sm border border-orange-200/50 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-orange-100 rounded-xl">
                <AlertCircle className="w-5 h-5 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-orange-900 mb-1">
                  Configuration requise
                </h3>
                <p className="text-sm text-orange-800 mb-4">
                  Pour recevoir les paiements PayPal, vous devez configurer vos identifiants API PayPal.
                </p>
                <Button
                  onClick={() => setShowForm(!showForm)}
                  icon={<DollarSign className="w-4 h-4" />}
                >
                  {showForm ? 'Annuler' : 'Configurer PayPal'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Statut - Activé */}
        {status && status.enabled && (
          <div className="bg-teal-50/50 backdrop-blur-sm border border-teal-200/50 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-teal-100 rounded-xl">
                <CheckCircle className="w-5 h-5 text-teal-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-teal-900 mb-1">
                  PayPal activé
                </h3>
                <p className="text-sm text-teal-800 mb-2">
                  Votre compte PayPal est configuré. Vous pouvez recevoir les paiements de vos factures.
                </p>
                <div className="flex flex-col gap-2 text-sm text-teal-700">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Mode : {status.mode === 'sandbox' ? 'Test (Sandbox)' : 'Production (Live)'}</span>
                  </div>
                  {status.hasMerchantId && (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      <span>Merchant ID configuré</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowForm(!showForm)}
                  >
                    Modifier la configuration
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDisable}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    Désactiver PayPal
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Formulaire de configuration */}
        {showForm && (
          <div className="bg-white/30 backdrop-blur-sm border border-white/40 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">
                Configuration PayPal
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleConfigure} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mode
                </label>
                <select
                  value={mode}
                  onChange={(e) => setMode(e.target.value as 'sandbox' | 'live')}
                  className={selectClasses}
                >
                  <option value="sandbox">Sandbox (Test)</option>
                  <option value="live">Live (Production)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1.5">
                  Utilisez Sandbox pour les tests, Live pour la production
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="Votre Client ID PayPal"
                  className={inputClasses}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Secret <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showSecret ? 'text' : 'password'}
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    placeholder="Votre Client Secret PayPal"
                    className={`${inputClasses} pr-12`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(!showSecret)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-teal-600 transition-colors"
                  >
                    {showSecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Merchant ID (optionnel)
                </label>
                <input
                  type="text"
                  value={merchantId}
                  onChange={(e) => setMerchantId(e.target.value)}
                  placeholder="Votre Merchant ID PayPal"
                  className={inputClasses}
                />
                <p className="text-xs text-gray-500 mt-1.5">
                  Le Merchant ID est optionnel mais recommandé
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="submit"
                  disabled={configuring}
                  icon={configuring ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  className="flex-1"
                >
                  {configuring ? 'Configuration en cours...' : 'Enregistrer'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowForm(false)}
                >
                  Annuler
                </Button>
              </div>
            </form>

            <div className="mt-4 p-4 bg-teal-50/50 border border-teal-200/50 rounded-xl">
              <p className="text-xs text-teal-800">
                <strong>Comment obtenir vos identifiants PayPal :</strong><br />
                1. Connectez-vous à votre <a href="https://developer.paypal.com" target="_blank" rel="noopener noreferrer" className="text-teal-600 underline hover:text-teal-700">compte développeur PayPal</a><br />
                2. Créez une application dans "My Apps & Credentials"<br />
                3. Copiez le Client ID et le Client Secret
              </p>
            </div>
          </div>
        )}

        {/* Information sur les frais */}
        <div className="bg-white/30 backdrop-blur-sm border border-white/40 rounded-2xl p-4">
          <h3 className="font-semibold text-gray-900 mb-2">
            Frais PayPal
          </h3>
          <p className="text-sm text-gray-700">
            Les frais PayPal standards s'appliquent (environ <span className="font-semibold text-teal-700">3,4% + 0,25€</span> par transaction).
            Ces frais sont directement prélevés par PayPal sur chaque paiement reçu.
          </p>
        </div>
      </div>
    </div>
  );
};
